<template>
  <div class="Dashboard">

    <Page
      :title="$t('Dashboard')"
      size="fullWidth"
    >

      <Layout>
        <LayoutSection>

          <SkeletonCard v-if="dashboardCounts === null" />
          <Card v-else :title="$t('NotificationsApp.Totals')">
            <CardSection class="stat-wrap stat-large">
              <div class="stat">
                <div class="stat-value">{{ addCommas(dashboardCounts.totalSubscribed) }}</div>
                <div class="stat-heading"><span class="nowrap">{{$t('NotificationsApp.Subscribers')}}</span></div>
              </div>

              <div class="stat">
                <div class="stat-value">{{ addCommas(dashboardCounts.totalActive) }}</div>
                <div class="stat-heading">{{$t('Active')}}</div>
              </div>

              <div class="stat stat-last">
                <div class="stat-value">{{ addCommas(dashboardCounts.totalUnsubscribed) }}</div>
                <div class="stat-heading">{{$t('NotificationsApp.Blocked')}}</div>
              </div>
            </CardSection>
          </Card>

          <Card>
            <CardSection>

              <FormLayout>
                <FormLayoutGroup condensed>
                  <DatePicker
                    :label="$t('Start Date')"
                    placeholder=""
                    v-model="filterStartDate"
                    model-format="YYYY-MM-DD"
                  />
                  <DatePicker
                    :label="$t('End Date')"
                    placeholder=""
                    v-model="filterEndDate"
                    model-format="YYYY-MM-DD"
                  />
                </FormLayoutGroup>
                <Button type="primary" @click="updateDateFilter">{{ $t('NotificationsApp.Filter Dates') }}</Button>
              </FormLayout>

            </CardSection>
          </Card>

          <SkeletonCard v-if="loading" />
          <template v-else>

            <SkeletonCard v-if="dashboardSubCounts === null" />
            <Card v-else :title="$t('NotificationsApp.By Date')">
              <CardSection class="stat-wrap">
                <div class="stat">
                  <div class="stat-value">{{ addCommas(dashboardSubCounts.totalRequests || 0) }}</div>
                  <div class="stat-heading nowrap">{{$t('NotificationsApp.Permission Requests')}}</div>
                </div>

                <div class="stat">
                  <div class="stat-value">{{ addCommas(dashboardSubCounts.totalGranted || 0) }}</div>
                  <div class="stat-heading">{{$t('NotificationsApp.Permissions Granted')}}</div>
                </div>

                <div class="stat">
                  <div class="stat-value">{{ addCommas(dashboardSubCounts.totalSent || 0) }}</div>
                  <div class="stat-heading">{{$t('NotificationsApp.Messages Sent')}}</div>
                </div>

                <div class="stat stat-last">
                  <div class="stat-value">{{ addCommas(dashboardSubCounts.totalInteractions || 0) }}</div>
                  <div class="stat-heading"><span class="nowrap">{{$t('NotificationsApp.User Interactions')}}</span></div>
                </div>
              </CardSection>
            </Card>

            <Card>
              <CardSection>

                <FormLayout>
                  <FormLayoutGroup condensed>
                    <Select
                      :label="$t('Type')"
                      v-model="filterType"
                      :options="filterTypeOptions"
                    />
                    <Select
                      :label="$t('Status')"
                      v-model="filterStatus"
                      :options="filterStatusOptions"
                    />
                    <Select
                      :label="$t('Domain')"
                      v-model="filterDomain"
                      :options="filterDomainOptions"
                    />
                    <Select
                      :label="$t('Language')"
                      v-model="filterLanguage"
                      :options="filterLanguageOptions"
                    />
                  </FormLayoutGroup>

                  <Filters
                    :query.sync="filterName"
                  />
                </FormLayout>

              </CardSection>
              <CardSection v-if="!filterType || filterType === 'manual'">

                <div class="section">
                  <h4>{{$t('NotificationsApp.Manual Notifications')}}</h4>

                  <DataTable
                    v-if="manualNotifications.length > 0"
                    :columns="columns"
                    :rows="dataTableFormat(manualNotifications)"
                    :totals="['', '', '',
                      this.totalManualSent,
                      this.totalManualClicked,
                      this.totalManualClicked / (this.totalManualSent || 1)
                    ]"
                    :pagination="{
                      offset: dataTableOffsetManual,
                      limit: limit,
                      total: manualNotifications.length,
                      updateOffset: val => dataTableOffsetManual = val,
                      updateLimit: updateLimit
                    }"
                    :emptyText="$t('NotificationsApp.No notifications sent')"
                  />

                  <div v-if="manualNotifications.length == 0" class="no-noticitaions">{{$t('NotificationsApp.No notifications sent')}}</div>
                </div>

              </CardSection>

              <CardSection v-if="!filterType || filterType === 'template'">
                <div class="section">
                  <h4>{{$t('NotificationsApp.Templates')}}</h4>

                  <DataTable
                    v-if="templateNotifications.length > 0"
                    :columns="columns"
                    :rows="dataTableFormat(templateNotifications)"
                    :totals="['', '', '',
                      this.totalTemplatesSent,
                      this.totalTemplatesClicked,
                      this.totalTemplatesClicked / (this.totalTemplatesSent || 1)
                    ]"
                    :pagination="{
                      offset: dataTableOffsetTemplates,
                      limit: limit,
                      total: templateNotifications.length,
                      updateOffset: val => dataTableOffsetTemplates = val,
                      updateLimit: updateLimit
                    }"
                    :emptyText="$t('NotificationsApp.No notifications sent')"
                  />

                  <div v-if="templateNotifications.length == 0" class="no-noticitaions">{{$t('NotificationsApp.No notifications sent')}}</div>
                </div>

              </CardSection>

            </Card>
          </template><!-- loading-->

        </LayoutSection>
      </Layout>

    </Page>
  </div>
</template>

<script>

import moment from 'moment'

export default {
  name: 'Dashboard',
  inject: ['store'],
  data() {
    return {
      loading: true,
      notifications: [],
      dashboardCounts: null,
      dashboardSubCounts: null,
      showAllManual: false,
      filterType: '',
      filterStatus: '',
      filterDomain: '',
      filterLanguage: '',
      filterStartDate: '',
      filterEndDate: '',
      filterName: '',

      dataTableOffsetManual: 0,
      dataTableOffsetTemplates: 0,
      limit: 10
    }
  },
  mounted() {
    this.filterStartDate = moment().tz(this.theUser.accountTimezone).subtract(1,'day').subtract(2, 'months').format('YYYY-MM-DD')
    this.filterEndDate = moment().tz(this.theUser.accountTimezone).subtract(1,'day').format('YYYY-MM-DD'),
    //
    this.doLoad()
    this.limit = this.getTableLimit('tableLimit') || 10
    this.api.notifications.getDashboardCount()
      .then((response) => {
        this.dashboardCounts = response.dashboardCounts
      })
  },
  methods: {
    updateDateFilter() {
      if (!this.filterStartDate || !this.filterEndDate) {
        this.$ui.alert({
          title: 'Error',
          content: 'Dates required'
        })
        return
      }
      this.doLoad()
    },
    doLoad() {
      NProgress.start()
      this.loading = true
      this.notifications = []
      const startDate = this.filterEndDate ? moment(this.filterStartDate).format('YYYY-MM-DD') : ''
      const endDate = this.filterStartDate ? moment(this.filterEndDate).format('YYYY-MM-DD') : ''
      this.api.notifications.getNotifications(startDate.substr(0, 10), endDate.substr(0, 10), true)
        .then((response) => {
          this.notifications = response.notifications
          this.dashboardSubCounts = response.dashboardCounts

          if (this.manualNotifications.length <= 10) {
            this.showAllManual = true
          }

          this.loading = false
        })
        .catch((error) => {
          this.apiCatch(error)
        })
        .finally(() => {
          NProgress.done()
        })
    },
    filterByFilters(notification) {
      if (this.filterType && notification.type.indexOf(this.filterType) === -1) {
        return false
      }
      if (this.filterStatus && notification.status !== this.filterStatus) {
        return false
      }
      if (this.filterDomain && notification.segments.domain && notification.segments.domain.length && !notification.segments.domain.includes(this.filterDomain)) {
        return false
      }
      if (this.filterLanguage && !notification.segments.browserLanguage.includes('any') && !notification.segments.browserLanguage.includes(this.filterLanguage)) {
        return false
      }
      if (this.filterName.trim() && notification.name.toLowerCase().indexOf(this.filterName.trim().toLowerCase()) === -1) {
        return false
      }
      return true
    },
    dataTableFormat(rows) {
      return rows.map(o => {
        const s = o.interactionStats
        return {
          name: o.name,
          domains: this.niceDomain(o.segments),
          languages: this.niceLanguage(o.segments),
          sent: o.deliveryStats.sent || 0,
          clicked: ((s.action1Clicked || 0) + (s.action2Clicked || 0) + (s.clicked || 0)) || 0,
          interacted: (((s.action1Clicked || 0) + (s.action2Clicked || 0) + (s.clicked || 0)) || 0) / (o.deliveryStats.sent || 1)
        }
      })
    },
    updateLimit(limit) {
      this.setTableLimit('tableLimit', limit)
      this.limit = limit
      this.offset = 0
    },
    arrayToOptions(arr) {
      return arr.map(val => ({
        label: val,
        value: val
      }))
    },
    percentage(p) {
      if (!p) return '0.00'
      if (!Number.isFinite(p)) return '0.00'
      if (Number.isNaN(p)) return '0.00'
      return (p * 100).toFixed(2);
    },
    niceDomain(segments) {
      if (!segments || !segments.domain || segments.domain.length === 0) {
        return this.$t('All')
      }
      return segments.domain.join(', ')
    },
    niceLanguage(segments) {
      if (!segments || !segments.browserLanguage || segments.browserLanguage.length === 0 || segments.browserLanguage[0] === '') {
        return '-'
      }
      let lang = segments.browserLanguage[0]
      if (lang === 'any') {
        return this.$t('Any')
      }
      if (!lang) {
        return '-'
      }
      return this.$t(`language.${lang}`)
    },
    addCommas(n) {
      let nn = n
      if (Number.isNaN(nn)) {
        nn = 0
      }
      nn += ''
      return nn.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
  },
  computed: {
    columns() {
      return [{
        key: 'name',
        label: this.$t('Name')
      },
      {
        key: 'domains',
        label: this.$t('Domain')
      },
      {
        key: 'languages',
        label: this.$t('Language')
      },
      {
        key: 'sent',
        label: this.$t('Sent'),
        format: 'integer'
      },
      {
        key: 'clicked',
        label: this.$t('Clicked'),
        format: 'integer'
      },
      {
        key: 'interacted',
        label: this.$t('Interacted'),
        format: 'percent'
      }]
    },
    filterTypeOptions() {
      return [
        { label: this.$t('All'), value: '' },
        { label: this.$t('Manual'), value: 'manual' },
        { label: this.$t('Template'), value: 'template' },
      ]
    },
    filterStatusOptions() { 
      return [
        { label: this.$t('All'), value: '' },
        { label: this.$t('Draft'), value: 'draft' },
        { label: this.$t('Active'), value: 'active' },
        { label: this.$t('Paused'), value: 'paused' },
        { label: this.$t('Scheduled'), value: 'scheduled' },
        { label: this.$t('Sent'), value: 'sent' },
      ]
    },
    filterDomainOptions() {
      let d = this.arrayToOptions(this.store.domains)
      return [{
        label: this.$t('All'),
        value: ''
      }, ...d]
    },
    filterLanguageOptions() {
      return this.store.languages.map(lang => {
        if (lang.value === 'any') {
          return {
            label: this.$t('language.any'),
            value: ''
          }
        }
        return {
          label: this.$t(`language.${lang.value}`),
          value: lang.value
        }
      })
    },
    manualNotifications() {
      return this.notifications.filter(o => o.type === 'manual' && o.status === 'sent').filter(this.filterByFilters).sort((a, b) => {
        if (Date.parse(a.sentOn) > Date.parse(b.sentOn)) return -1
        if (Date.parse(a.sentOn) < Date.parse(b.sentOn)) return 1
        return 0
      })
    },
    manualNotificationsSlice() {
      return this.manualNotifications.slice(0, 10)
    },
    templateNotifications() {
      return this.notifications.filter(o => o.type === 'template' && (o.status !== 'draft')).filter(this.filterByFilters)
    },
    totalTemplatesSent() {
      let c = 0
      this.templateNotifications.forEach((n) => {
        c += (n.deliveryStats.sent || 0)
      })
      return c
    },
    totalTemplatesClicked() {
      let c = 0
      this.templateNotifications.forEach((n) => {
        c += (n.interactionStats.action1Clicked || 0) + (n.interactionStats.action2Clicked || 0) + (n.interactionStats.clicked || 0)
      })
      return c
    },
    totalManualSent() {
      let c = 0
      this.manualNotifications.forEach((n) => {
        c += (n.deliveryStats.sent || 0)
      })
      return c
    },
    totalManualDelivered() {
      let c = 0
      this.manualNotifications.forEach((n) => {
        c += (n.deliveryStats.delivered || 0)
      })
      return c
    },
    totalManualClicked() {
      let c = 0
      this.manualNotifications.forEach((n) => {
        c += (n.interactionStats.action1Clicked || 0) + (n.interactionStats.action2Clicked || 0) + (n.interactionStats.clicked || 0)
      })
      return c
    },
  },
};
</script>

<style scoped lang="less">
.right {
  text-align: right;
}
.nowrap {
  white-space: nowrap;
}

.stat-wrap {
  display: flex;
  &.stat-large {
    font-size: 150%;
  }
}
.stat {
  
  flex: 1 1 0px;
  text-align: center;
  padding: 15px 25px;
  height: 70px;
  border-right: 1px solid #f3f3f3;

  &.stat-last {
    border-right: none;
  }

  .stat-value {
    font-size: 120%;
    padding-left: 0;
  }
  .stat-sub-value {
    font-size: 90%;
  }
  .stat-heading {
    font-size: 80%;
  }
}

.no-noticitaions {
  margin: 15px;
}

tfoot {
  td {
    font-weight: bold;
  }
}
.el-date-editor.el-input {
  width: 100%;
}
</style>
